/**
 * UIModeToggle — Pill switch en el topbar para cambiar entre Simple / Avanzado.
 * Con tooltip explicativo. Persiste vía `useUIMode()`.
 */
import { Sparkles, Wrench } from "lucide-react";
import { useUIMode } from "../../hooks/useUIMode";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

export function UIModeToggle({ compact = false }) {
  const { mode, setMode, loading } = useUIMode();
  if (loading) return null;

  const isSimple = mode === "simple";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="hidden sm:inline-flex items-center rounded-full border bg-muted/40 p-0.5 text-xs"
            data-testid="ui-mode-toggle"
          >
            <button
              type="button"
              onClick={() => setMode("simple", "topbar-toggle")}
              className={`flex items-center gap-1 rounded-full px-2 py-1 transition-colors ${
                isSimple
                  ? "bg-primary text-primary-foreground font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="ui-mode-simple-btn"
              aria-pressed={isSimple}
            >
              <Sparkles className="w-3 h-3" />
              {!compact && <span>Simple</span>}
            </button>
            <button
              type="button"
              onClick={() => setMode("advanced", "topbar-toggle")}
              className={`flex items-center gap-1 rounded-full px-2 py-1 transition-colors ${
                !isSimple
                  ? "bg-primary text-primary-foreground font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="ui-mode-advanced-btn"
              aria-pressed={!isSimple}
            >
              <Wrench className="w-3 h-3" />
              {!compact && <span>Avanzado</span>}
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[260px] p-2">
          <div className="font-medium text-xs mb-1">
            {isSimple ? "Modo Simple activo" : "Modo Avanzado activo"}
          </div>
          <div className="text-[11px] leading-snug">
            {isSimple
              ? "Vemos solo lo esencial. Las opciones avanzadas se muestran bajo un toggle."
              : "Todas las opciones expandidas por defecto. Ideal si ya dominas la app."}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
